import { useReactiveVar } from '@apollo/client';
import AccountContext from 'context/AccountContext';
import FeatureFlagsContext from 'context/FeatureFlagsContext';
import { useContext, useMemo } from 'react';
import { parkingPaymentCardId } from 'states/common';
import { featureCountryCode } from 'states/persistInStorage';
import {
  Card,
  CompanyBenefit,
  Maybe,
  PaymentOption,
} from 'types/generatedSchemaTypes';
import { isMobilePayEnabledForCountry } from 'utils/featureFlagUtils';
import {
  cardErrorStatuses,
  enrichPaymentOptionWithName,
  isCardSelectable,
} from 'utils/paymentUtils';
import useExternalPaymentMethods, {
  ExternalPaymentWrapper,
} from './useExternalPaymentMethods';
import useSelectedCompanyBenefit from './useSelectedCompanyBenefit';

export type SelectedPaymentMethod = Partial<
  Omit<Card, '__typename' | 'isDefault'>
> &
  Partial<Omit<CompanyBenefit, '__typename' | 'countryCode'>> &
  Partial<Omit<ExternalPaymentWrapper, '__typename'>> &
  Partial<Omit<PaymentOption, '__typename' | 'isDefault'>>;

const useSelectedPaymentMethod = (
  cardId?: string,
  parkingZoneUid?: string
): SelectedPaymentMethod => {
  const {
    customer: { paymentCards, paymentOptions, companyBenefits, contactEmail },
  } = useContext(AccountContext);
  const {
    flags: { enable_mobilepay_by_country },
  } = useContext(FeatureFlagsContext);
  const appCountryCode = useReactiveVar(featureCountryCode);
  const showMobilePay = isMobilePayEnabledForCountry(
    enable_mobilepay_by_country,
    appCountryCode,
    contactEmail
  );
  const selectedPaymentOptionId = useReactiveVar(parkingPaymentCardId);
  const paymentOptionId = cardId ?? selectedPaymentOptionId;

  const availablePaymentOptions = paymentOptions?.filter(
    (option) => option?.method !== 'MOBILEPAY' || showMobilePay
  );

  const defaultOptionId = paymentOptionId
    ? undefined
    : availablePaymentOptions?.find(
        (option) => option?.isDefaultTokenizedPaymentOption
      )?.optionId;

  const paymentCard = paymentCards?.find((card: Maybe<Card>) => {
    // Fall back to card.isDefault for accounts without paymentOptions yet
    const matchesDefault =
      card?.cardId === defaultOptionId ||
      (!paymentOptionId && !defaultOptionId && card?.isDefault === true);
    // Nets selectability is unchanged: any non-error status (incl. Verifying)
    // stays selectable. Adyen is stricter — a pending (Adding/Verifying) Adyen
    // card is not chargeable until webhook activation, so only Active is
    // selectable. Keeps the flag-off Nets path byte-for-byte unchanged.
    const isAdyenCard =
      card?.paymentProvider === 'ADYEN_SE' ||
      card?.paymentProvider === 'ADYEN_FI';
    const selectable = isAdyenCard
      ? isCardSelectable(card?.status)
      : !cardErrorStatuses.includes(card?.status || 'Active');
    return (card?.cardId === paymentOptionId || matchesDefault) && selectable;
  });

  // Check for selected payment option and enrich with optionName
  const paymentOption = availablePaymentOptions?.find(
    (option: Maybe<PaymentOption>) => {
      return (
        option?.optionId === paymentOptionId ||
        (!paymentOptionId && option?.isDefaultTokenizedPaymentOption === true)
      );
    }
  );

  // Enrich payment option with optionName from companyBenefits using utility function
  const enrichedPaymentOption = enrichPaymentOptionWithName(
    paymentOption || null,
    companyBenefits
  );

  const { companyBenefit } = useSelectedCompanyBenefit(parkingZoneUid);
  const { externalPaymentMethod } = useExternalPaymentMethods();

  return useMemo(
    () =>
      ({
        ...companyBenefit,
        ...paymentCard,
        ...externalPaymentMethod,
        ...enrichedPaymentOption,
      }) as SelectedPaymentMethod,
    [companyBenefit, paymentCard, externalPaymentMethod, enrichedPaymentOption]
  );
};

export default useSelectedPaymentMethod;
